'use client'

/**
 * CardHydrator — the island that unforks the /data product rails (FRIDGE-449).
 *
 * The site-standard ProductGridCard is a client component whose import pulls
 * the embla carousel, the badge list, the star row and the signed-offer link
 * into any page that uses it — ~40 kB that put seven /data pages over the
 * First Load JS budget when the rails first tried it (CI, 08/17/2026). The
 * fork this replaces solved that by rendering a static lookalike, which broke
 * the owner's one-card-sitewide standard instead.
 *
 * This component keeps BOTH: it renders its children — the server-rendered
 * static first frame, zero client JS beyond this ~1 kB shell — until the card
 * scrolls near the viewport, then dynamic-imports the real ProductGridCard
 * and swaps it in. The interactive chunk is loaded on demand, so it never
 * appears in a route's First Load JS column; the budget stays green with NO
 * budget line raised, and every surface renders the same card.
 *
 * The swap is visually seamless by construction: the static frame is the
 * card's own first frame (same image box, same brand/title/price rows), so
 * hydration adds the carousel arrows, star row, badges and CTA without
 * moving what was already painted.
 */
import { useEffect, useRef, useState, type ComponentProps, type ReactNode } from 'react'
import dynamic from 'next/dynamic'

const ProductGridCard = dynamic(
  () => import('./product-grid-card').then((m) => m.ProductGridCard),
  { ssr: false },
)

type Props = ComponentProps<typeof ProductGridCard> & { children: ReactNode }

export function CardHydrator({ children, ...card }: Props) {
  const ref = useRef<HTMLDivElement>(null)
  const [live, setLive] = useState(false)

  useEffect(() => {
    const el = ref.current
    if (!el) return
    // No-IO browsers hydrate on the next frame — asynchronously, so the effect
    // itself never sets state mid-render (react-hooks/set-state-in-effect).
    if (typeof IntersectionObserver === 'undefined') {
      const t = setTimeout(() => setLive(true), 0)
      return () => clearTimeout(t)
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) { setLive(true); io.disconnect() }
      },
      // Start loading one viewport early so the swap lands before the eye does.
      { rootMargin: '100% 0px' },
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return <div ref={ref}>{live ? <ProductGridCard {...card} /> : children}</div>
}
